import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import './QuestWaking.css'

/**
 * Quest Waking Sequence
 *
 * "Luna doesn't see dead time - she sees a character who stopped caring about her."
 * — Diego Ramirez
 *
 * 4-frame animation for greeting load (2 seconds total):
 * - Frame 1 (0-400ms): Sleeping - eyes closed, muted colors, breathing
 * - Frame 2 (400-800ms): Stirring - eyes half-open, colors warming
 * - Frame 3 (800-1400ms): Stretching - eyes open, looking up, scale 1.05
 * - Frame 4 (1400-2000ms): Ready! - eyes bright with sparkle, glow
 *
 * Extended load (>2s): Loops frames 2-3, never returns to sleeping
 *
 * @param {boolean} isLoading - Whether Quest is loading
 * @param {function} onReady - Called when waking animation completes (if not extended)
 */

const FRAME_TIMINGS = {
  sleeping: 400,    // 0-400ms
  stirring: 400,    // 400-800ms
  stretching: 600,  // 800-1400ms
  ready: 600,       // 1400-2000ms
}

const TOTAL_WAKE_TIME = 2000 // ms

export default function QuestWaking({ isLoading, onReady }) {
  const [frame, setFrame] = useState('sleeping')
  const [isExtended, setIsExtended] = useState(false)
  const startTimeRef = useRef(null)
  const animationRef = useRef(null)

  useEffect(() => {
    if (!isLoading) {
      // Reset when not loading
      setFrame('sleeping')
      setIsExtended(false)
      startTimeRef.current = null
      if (animationRef.current) {
        clearTimeout(animationRef.current)
      }
      return
    }

    // Start the waking sequence
    startTimeRef.current = Date.now()

    // Frame 1 -> Frame 2
    const stirringTimer = setTimeout(() => {
      setFrame('stirring')
    }, FRAME_TIMINGS.sleeping)

    // Frame 2 -> Frame 3
    const stretchingTimer = setTimeout(() => {
      setFrame('stretching')
    }, FRAME_TIMINGS.sleeping + FRAME_TIMINGS.stirring)

    // Frame 3 -> Frame 4 (or extended loop)
    const readyTimer = setTimeout(() => {
      // Check if we're still loading after 2 seconds
      if (isLoading) {
        // Still loading - enter extended mode
        setIsExtended(true)
      } else {
        setFrame('ready')
        onReady?.()
      }
    }, FRAME_TIMINGS.sleeping + FRAME_TIMINGS.stirring + FRAME_TIMINGS.stretching)

    return () => {
      clearTimeout(stirringTimer)
      clearTimeout(stretchingTimer)
      clearTimeout(readyTimer)
    }
  }, [isLoading])

  // Handle transition to ready when loading finishes
  useEffect(() => {
    if (!isLoading && frame !== 'sleeping' && frame !== 'ready') {
      // Loading finished mid-animation - go to ready
      setFrame('ready')
      setIsExtended(false)
      onReady?.()
    }
  }, [isLoading, frame, onReady])

  // Extended mode: loop between stirring and stretching
  useEffect(() => {
    if (!isExtended) return

    const loopFrames = () => {
      setFrame(prev => prev === 'stirring' ? 'stretching' : 'stirring')
    }

    // Loop every 800ms (400ms per frame)
    const loopInterval = setInterval(loopFrames, 800)

    return () => clearInterval(loopInterval)
  }, [isExtended])

  // Frame-specific animation properties
  const frameConfig = {
    sleeping: {
      emoji: '😴',
      scale: 0.98,
      saturation: 0.8,
      brightness: 1.0,
      breathe: true,
    },
    stirring: {
      emoji: '😊',
      scale: 1.0,
      saturation: 0.9,
      brightness: 1.0,
      sway: true,
    },
    stretching: {
      emoji: '🙌',
      scale: 1.05,
      saturation: 1.0,
      brightness: 1.0,
      stretch: true,
    },
    ready: {
      emoji: '✨',
      scale: 1.0,
      saturation: 1.0,
      brightness: 1.05,
      sparkle: true,
    },
  }

  const config = frameConfig[frame]

  return (
    <div className="quest-waking">
      <motion.div
        className={`quest-waking-avatar ${frame}`}
        animate={{
          scale: config.breathe ? [0.98, 1.0, 0.98] : config.scale,
          filter: `saturate(${config.saturation}) brightness(${config.brightness})`,
        }}
        transition={{
          scale: config.breathe ? {
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          } : {
            duration: 0.3,
            ease: 'easeOut'
          },
          filter: {
            duration: 0.3,
            ease: 'easeOut'
          }
        }}
      >
        {/* Quest emoji face */}
        <motion.span
          className="quest-face"
          animate={{
            rotate: config.sway ? [-3, 3, -3] : 0,
            y: config.stretch ? -5 : 0,
          }}
          transition={{
            rotate: config.sway ? {
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            } : { duration: 0.2 },
            y: { duration: 0.3, ease: 'easeOut' }
          }}
        >
          🗺️
        </motion.span>

        {/* Sparkle overlay for ready state */}
        {config.sparkle && (
          <motion.span
            className="quest-sparkle"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            ✨
          </motion.span>
        )}

        {/* Status indicator */}
        <motion.span
          className="quest-status-emoji"
          key={config.emoji}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {config.emoji}
        </motion.span>
      </motion.div>

      {/* Status text */}
      <motion.p
        className="quest-waking-text"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {frame === 'sleeping' && 'Quest is waking up...'}
        {frame === 'stirring' && 'Quest is waking up...'}
        {frame === 'stretching' && 'Quest is almost ready...'}
        {frame === 'ready' && 'Quest is ready!'}
        {isExtended && 'Quest is on the way...'}
      </motion.p>
    </div>
  )
}
